// Withdrawal request: the care team asks to withdraw a patient CMS still holds in its 90-day
// lock-in. WF67 writes it as a coordinator task. Spec: docs/specs/2026-10-19-withdrawal-request-during-lock-in.md.
import { isPausingRequest, WH_PERIOD_STATUSES } from '../../../scripts/withdrawal-pause.mjs';
import { decodeWithdrawalReason } from './withdrawalReason';
import { readTaskContext, templateCodeOf } from './task';

// The workflows' own test of a request that holds its patient (scripts/ ships in the bundle; S-182 pins it).
export { isPausingRequest };

// The rollback switch: off stops new requests, while open ones stay visible and cancellable.
export const WITHDRAWAL_REQUEST_ON = true;
export const WITHDRAWAL_REQUEST_CODE = 'cc_unalign_after_lockin';
// S-181 compares this literal with WF67's.
export const LOCK_IN_DAYS = 90;

const DAY_MS = 86_400_000;
// S-181 compares this literal with WF67's.
const OPEN_STATUSES = new Set(['pending', 'scheduled', 'in_progress', 'overdue']);

export interface LockInEnd {
  ymd: string;
  /** 12:00 UTC on `ymd` — the instant WF67 makes the task due. */
  at: number;
}

interface PeriodLike {
  cms_alignment_date?: unknown;
}

// cms_alignment_date is a date-only column, so the arithmetic stays in UTC.
export function lockInEnd(carePeriod: PeriodLike | null | undefined, nowMs: number = Date.now()): LockInEnd | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(carePeriod?.cms_alignment_date ?? ''));
  if (!m) return null;
  const aligned = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(aligned) || aligned > nowMs) return null;
  const ymd = new Date(aligned + LOCK_IN_DAYS * DAY_MS).toISOString().slice(0, 10);
  return { ymd, at: Date.parse(`${ymd}T12:00:00Z`) };
}

export function isLockInOver(end: LockInEnd | null, nowMs: number): boolean {
  return !!end && nowMs >= end.at;
}

// A calendar date, checked by round trip: the regex alone lets 2026-02-30 through.
export function validYmd(value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const ms = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === value ? value : null;
}

export function formatLockInDate(ymd: string): string {
  return new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

interface TaskRowLike {
  status?: unknown;
  id_care_period?: unknown;
  care_period_id?: unknown;
  context_json?: unknown;
  template?: { template_code?: string } | null;
  template_code?: string | null;
}

export function isWithdrawalRequest(task: TaskRowLike | null | undefined): boolean {
  return templateCodeOf(task) === WITHDRAWAL_REQUEST_CODE;
}

export function isOpenWithdrawalRequest(task: TaskRowLike | null | undefined): boolean {
  return isWithdrawalRequest(task) && OPEN_STATUSES.has(String(task?.status ?? ''));
}

/** The open request of this care period, or null. Rows may come in any status. */
export function openWithdrawalRequest<T extends TaskRowLike>(rows: readonly T[] | null | undefined, carePeriodId: unknown): T | null {
  if (!Array.isArray(rows) || carePeriodId == null || carePeriodId === '') return null;
  const want = String(carePeriodId);
  return rows.find((t) => isOpenWithdrawalRequest(t)
    && String(t.id_care_period ?? t.care_period_id ?? '') === want) ?? null;
}

/** The open request to show: this period's first, else one left open on another period. */
export function anyOpenWithdrawalRequest<T extends TaskRowLike>(
  rows: readonly T[] | null | undefined,
  preferCarePeriodId?: unknown,
): T | null {
  if (!Array.isArray(rows)) return null;
  return openWithdrawalRequest(rows, preferCarePeriodId)
    ?? rows.find((t) => isOpenWithdrawalRequest(t)) ?? null;
}

/** The request's own care period, which the cancel must name. */
export function requestCarePeriodId(task: TaskRowLike | null | undefined): string | null {
  const id = task?.id_care_period ?? task?.care_period_id;
  return id == null || id === '' ? null : String(id);
}

// Program_Archive_Patient sets the category to PROGRAM_CATEGORY_ARCHIVE (-2); getpatient returns it.
export const ARCHIVED_CATEGORY = -2;

/** null when the category was not read. */
export function isArchivedInProgram(patient: { category?: unknown } | null | undefined): boolean | null {
  if (!patient || patient.category == null || patient.category === '') return null;
  const n = Number(patient.category);
  return Number.isFinite(n) ? n === ARCHIVED_CATEGORY : null;
}

interface PeriodRef {
  care_period_id?: unknown;
  status?: unknown;
}

/** Whether a request on this period could hold the patient at all (the gates' period statuses). */
export function periodCanHold(carePeriod: PeriodRef | null | undefined): boolean {
  return WH_PERIOD_STATUSES.includes(String(carePeriod?.status ?? ''));
}

// The patient file's one-line summary of a hold. The charts stop only once the patient is archived (the window offers it).
export function holdSummary(chartsLive: boolean): string {
  return chartsLive
    ? 'No automated messages or alerts until the request is cancelled or the withdrawal is done. New readings still reach the charts.'
    : 'No automated messages, readings or alerts until the request is cancelled or the withdrawal is done.';
}

/** "Oct 27, 2026", or null when the row carries no readable date. */
export function requestedOnLabel(task: { created?: unknown; created_at?: unknown } | null | undefined): string | null {
  const ms = Date.parse(String(task?.created ?? task?.created_at ?? ''));
  return Number.isNaN(ms) ? null : new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// As the gates decide: the exact row WF67 writes, on this period, while the period holds. The patient file's period is
// this program's, so a request left on another period or by the other program (BETA and PROD share the database) holds nothing.
export function holdsOnPeriod(request: unknown, carePeriod: PeriodRef | null | undefined): boolean {
  if (!request || !carePeriod || carePeriod.care_period_id == null) return false;
  return isPausingRequest(request)
    && requestCarePeriodId(request as TaskRowLike) === String(carePeriod.care_period_id)
    && periodCanHold(carePeriod);
}

export type ChartsMismatch = 'stop' | 'resume' | null;

// The workflows cannot see the archive, so the app is the one place the two halves of a hold can disagree.
export function chartsMismatch(holding: boolean, archived: boolean | null): ChartsMismatch {
  if (archived == null) return null;
  if (holding && !archived) return 'stop';
  if (!holding && archived) return 'resume';
  return null;
}

export type SaveRequestOutcome = 'not_recorded' | 'saved' | 'saved_not_archived' | 'saved_no_hold';

// Archive only once a genuine request is read back: a request that holds nothing must not stop the charts.
export async function saveRequestThenArchive({ request, archive }: {
  request: () => Promise<{ confirmed: boolean; task: unknown }>;
  archive: () => Promise<unknown>;
}): Promise<SaveRequestOutcome> {
  const outcome = await request().catch(() => null);
  if (!outcome?.confirmed) return 'not_recorded';
  if (!isPausingRequest(outcome.task)) return 'saved_no_hold';
  try {
    await archive();
    return 'saved';
  } catch {
    return 'saved_not_archived';
  }
}

export type CancelRequestOutcome = 'not_sent' | 'not_unarchived' | 'cancel_failed' | 'not_confirmed' | 'cancelled';

// Un-archive FIRST: a failed un-archive then sends nothing, rather than resuming messages to a patient whose
// charts stay stopped. `unarchived` is a latch, so a retry after a failed event goes straight to the event.
export async function unarchiveThenCancel({ unarchive, cancel, unarchived = false }: {
  unarchive: () => Promise<unknown>;
  cancel: () => Promise<{ confirmed: boolean }>;
  unarchived?: boolean;
}): Promise<{ outcome: CancelRequestOutcome; unarchived: boolean }> {
  if (!unarchived) {
    try {
      await unarchive();
    } catch {
      return { outcome: 'not_unarchived', unarchived: false };
    }
  }
  try {
    const res = await cancel();
    return { outcome: res?.confirmed ? 'cancelled' : 'not_confirmed', unarchived: true };
  } catch {
    return { outcome: 'cancel_failed', unarchived: true };
  }
}

export interface WithdrawalRequestView {
  reasonCode: string | null;
  reasonLabel: string;
}

// The task is writable through WF5's unauthenticated door, so only a vocabulary code is shown.
export function withdrawalRequestOf(task: TaskRowLike | null | undefined): WithdrawalRequestView | null {
  if (!isWithdrawalRequest(task)) return null;
  const ctx = readTaskContext(task?.context_json);
  const decoded = decodeWithdrawalReason(typeof ctx?.withdrawal_reason === 'string' ? ctx.withdrawal_reason : null);
  if (decoded?.kind === 'operational' && decoded.code && decoded.label) {
    return { reasonCode: decoded.code, reasonLabel: decoded.label };
  }
  return { reasonCode: null, reasonLabel: 'Reason not recorded' };
}

export type UnalignedWait = 'unaligned' | 'timeout' | 'stopped';

export interface WaitForUnalignedOpts {
  refresh: () => Promise<{ status?: unknown } | null | undefined>;
  isAlive: () => boolean;
  sleep?: (ms: number) => Promise<void>;
  pollMs?: number;
  max?: number;
}

// WF24 writes CMS's verdict before the care period, so poll until the period itself says `unaligned`.
export async function waitForUnaligned({
  refresh, isAlive, sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }), pollMs = 2500, max = 12,
}: WaitForUnalignedOpts): Promise<UnalignedWait> {
  const poll = async (left: number): Promise<UnalignedWait> => {
    if (left <= 0) return 'timeout';
    await sleep(pollMs);
    if (!isAlive()) return 'stopped';
    const cp = await refresh().catch(() => null);
    if (!isAlive()) return 'stopped';
    return cp?.status === 'unaligned' ? 'unaligned' : poll(left - 1);
  };
  return poll(max);
}
